import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useRouteLoaderData,
} from "@remix-run/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Button } from "~/components/ui/Button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/Input";
import { Textarea } from "~/components/ui/textarea";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData, isEnabled } from "~/lib/utils";

export const sitemap = createSitemapFunction("events");

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const { t } = useTranslation();
  return getMetaData(data, {
    title: t("Eventi"),
    description: t(
      "Partecipa ai nostri eventi esclusivi: degustazioni, workshop e serate a tema per un'esperienza di benessere indimenticabile. Scopri il calendario e prenota il tuo posto."
    ),
  });
};
export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "eventi";

    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export async function action({ request, context }: ActionFunctionArgs) {
  if (request.method !== "POST") {
    return Response.json({ error: "Method Not Allowed" }, { status: 405 });
  }

  try {
    const formData = await request.formData();
    const formId = formData.get("form-id") as string;
    const email = formData.get("email") as string;
    const name = formData.get("name") as string;
    const phone = formData.get("phone") as string;
    const message = formData.get("message") as string;
    const object = formData.get("object") as string;
    const privacy = formData.get("privacy") === "on";
    const unitTag = formData.get("_wpcf7_unit_tag") as string;

    const ContactFormSchema = Yup.object().shape({
      email: Yup.string().email("Invalid email").required("Required"),
      name: Yup.string().required("Required"),
      phone: Yup.string().required("Required"),
      message: Yup.string().required("Required"),
      object: Yup.string().required("Required"),
      unitTag: Yup.string().required("Required"),
      privacy: Yup.boolean().oneOf([true], "You must agree to privacy policy"),
    });

    try {
      await ContactFormSchema.validate({
        name,
        phone,
        email,
        message,
        object,
        unitTag,
        privacy,
      });
    } catch (validationError: any) {
      return Response.json(
        {
          error: "Validation failed",
          fieldErrors: { [validationError.path]: validationError.message },
        },
        { status: 400 }
      );
    }

    const contactFormResp = await fetch(
      `${context.cloudflare.env.WP_API_HOST}wp-json/contact-form-7/v1/contact-forms/${formId}/feedback`,
      {
        method: "POST",
        body: formData,
      }
    );

    const contactFormRespJson: any = await contactFormResp.json();

    if (contactFormResp.ok && contactFormRespJson.status === "mail_sent") {
      return Response.json({
        error: null,
        message: "Message sent successfully",
        sent: true,
      });
    } else {
      console.error("Error: ", contactFormRespJson);
      return Response.json({
        error: "An Error happened sending the form data",
      });
    }
  } catch (err: any) {
    console.error("Error: ", err);
  }
  return Response.json({
    error: null,
    fieldErrors: {
      name: null,
      phone: null,
      email: null,
      object: null,
      message: null,
    },
  });
}

export default function Events() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();
  const { bookingDisabled } = useRouteLoaderData<any>("root");
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedInterest, setSelectedInterest] =
    useState("esperienze-private");
  const navigation = useNavigation();

  const actionData = useActionData<any>();
  useEffect(() => {
    if (actionData?.sent) {
      setShowSuccess(true);
    } else {
      setShowSuccess(false);
    }
  }, [actionData]);

  const isSubmitting = navigation.state === "submitting";

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="flex flex-col lg:pb-12">
        {loaderData.page.acf?.hero && (
          <section
            className="bg-cover bg-center bg-no-repeat "
            style={{
              backgroundImage: `url('/cms-asset/${loaderData.page.acf?.hero.image?.id}')`,
            }}
          >
            <div className="bg-black bg-opacity-40 py-32">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {loaderData.page.acf?.hero.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <p className="text-white text-lg mt-4">
                  {loaderData.page.acf?.hero.description}
                </p>
              </div>
            </div>
          </section>
        )}

        {[
          loaderData.page.acf?.info_section,
          loaderData.page.acf?.info_section_2,
          loaderData.page.acf?.info_section_3,
        ]
          .filter(Boolean)
          .map((section, index) => {
            const isImageLeft = index % 2 === 0;
            return (
              <section key={index} className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
                  {isImageLeft ? (
                    <>
                      <div>
                        <img
                          alt=""
                          src={`/cms-asset/${section.image?.id}`}
                          className="w-full h-96 object-cover object-center"
                        />
                      </div>
                      <div className="flex flex-col">
                        <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                          {section.title}
                        </h4>
                        <div
                          className="mt-4"
                          dangerouslySetInnerHTML={{
                            __html: section.description,
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col">
                        <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                          {section.title}
                        </h4>
                        <div
                          className="mt-4"
                          dangerouslySetInnerHTML={{
                            __html: section.description,
                          }}
                        />
                      </div>
                      <div>
                        <img
                          alt=""
                          src={`/cms-asset/${section.image?.id}`}
                          className="w-full h-96 object-cover object-center"
                        />
                      </div>
                    </>
                  )}
                </div>
              </section>
            );
          })}

        {loaderData.page.acf?.contact_section &&
          isEnabled(
            loaderData.page.acf?.contact_section.contact_section_enable
          ) && (
            <section className="bg-white-nude">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
                <div>
                  <h4 className="text-secondary font-tenor-sans text-xl lg:text-4xl uppercase lg:max-w-sm">
                    {loaderData.page.acf?.contact_section.title}
                  </h4>
                  <div
                    className="mt-4 text-sm lg:text-xl"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.contact_section.description,
                    }}
                  />
                </div>
                <Form
                  method="post"
                  className="bg-white border border-secondary/20 p-6 mt-8"
                >
                  <input
                    type="hidden"
                    name="form-id"
                    value={loaderData.page.acf.contact_form_id}
                  />
                  <input
                    type="hidden"
                    name="_wpcf7_unit_tag"
                    value={`wpcf7-f-${
                      loaderData.page.acf.contact_form_id
                    }-${Math.random().toString(36).substring(7).toUpperCase()}`}
                  />
                  <input type="hidden" name="object" value={selectedInterest} />
                  {actionData?.error && (
                    <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 w-full">
                      <p className="text-destructive text-sm text-center">
                        {actionData.error}
                      </p>
                    </div>
                  )}

                  {showSuccess && (
                    <div className="mb-4 p-3 bg-green-200 w-full">
                      <p className="text-green-600 text-sm text-center">
                        {t("Messaggio inviato con successo")}
                      </p>
                    </div>
                  )}

                  <div className="mb-6">
                    <label className="block text-secondary text-base font-medium mb-3">
                      {t("Sono interessato a")}:
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedInterest("esperienze-private")
                        }
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                          selectedInterest === "esperienze-private"
                            ? "bg-secondary text-white"
                            : "bg-white border border-secondary text-secondary"
                        }`}
                      >
                        {t("Esperienze private")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedInterest("eventi-aziendali")}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                          selectedInterest === "eventi-aziendali"
                            ? "bg-secondary text-white"
                            : "bg-white border border-secondary text-secondary"
                        }`}
                      >
                        {t("Eventi aziendali su misura")}
                      </button>
                    </div>
                    {actionData?.fieldErrors?.object && (
                      <div className="text-destructive text-sm mt-1">
                        {actionData.fieldErrors.object}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-secondary text-sm font-medium mb-1"
                      >
                        {t("Il tuo nome")}
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Mario rossi"
                        required
                      />
                      {actionData?.fieldErrors?.name && (
                        <div className="text-destructive text-sm mt-1">
                          {actionData.fieldErrors.name}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-secondary text-sm font-medium mb-1"
                      >
                        {t("Numero di telefono")}
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+393300000000"
                        required
                      />
                      {actionData?.fieldErrors?.phone && (
                        <div className="text-destructive text-sm mt-1">
                          {actionData.fieldErrors.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="block text-secondary text-sm font-medium mb-1"
                    >
                      {t("La tua email")}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="email@gmail.com"
                      required
                      autoComplete="email"
                    />
                    {actionData?.fieldErrors?.email && (
                      <div className="text-destructive text-sm mt-1">
                        {actionData.fieldErrors.email}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="message"
                      className="block text-secondary text-sm font-medium mb-1"
                    >
                      {t("Fornisci ulteriori dettagli")}
                    </label>
                    <Textarea
                      name="message"
                      id="message"
                      required
                      rows={4}
                      placeholder={""}
                    />
                    {actionData?.fieldErrors?.message && (
                      <div className="text-destructive text-sm mt-1">
                        {actionData.fieldErrors.message}
                      </div>
                    )}
                  </div>

                  <div className="mb-4 flex flex-row items-center space-x-2">
                    <Checkbox name="privacy" id="privacy" required />
                    <label
                      htmlFor="privacy"
                      className="block text-sm font-medium"
                    >
                      {t("Inviando il messaggio accetto la")}{" "}
                      <Link
                        target="_blank"
                        className="text-secondary underline"
                        to={getMenuHref(i18n.language, "/privacy-policy")}
                      >
                        privacy policy
                      </Link>
                    </label>
                  </div>

                  <Button
                    variant={"secondary"}
                    className="w-full"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {t("Invia messaggio")}
                  </Button>
                </Form>
              </div>
            </section>
          )}

        {loaderData.page.acf?.book_section && (
          <section className="max-w-7xl mx-auto w-full lg:mt-12">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.book_section?.image?.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-2xl lg:text-4xl text-center font-tenor-sans text-white uppercase">
                    {loaderData.page.acf?.book_section?.title}
                  </h1>
                  <div
                    className="text-white mt-4 text-xl text-center"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.book_section.description,
                    }}
                  />
                  <Link
                    to={
                      bookingDisabled
                        ? getMenuHref(i18n.language, "/coming-soon")
                        : getMenuHref(i18n.language, "/booking")
                    }
                  >
                    <Button variant={"muted"} className="mt-4">
                      {t("Prenota la tua esperienza")}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
